import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, CheckCircle2, Circle, Clock } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { api, formatApiError } from "../../lib/api";

const TASK_LABELS = { a_faire: "À faire", en_cours: "En cours", termine: "Terminée" };
const VENDOR_LABELS = { a_contacter: "À contacter", contacte: "Contacté", confirme: "Confirmé", annule: "Annulé" };
const PRIORITY_COLORS = { haute: "text-red-600", normale: "text-deepgreen", basse: "text-muted-foreground" };

export default function CoordinationDetailPage() {
  const { dossierId } = useParams();
  const [data, setData] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", due_date: "", priority: "normale", internal: false });
  const [vendorForm, setVendorForm] = useState({ name: "", category: "", contact: "" });

  const load = () => api.get(`/admin/coordination/${dossierId}`).then((r) => setData(r.data)).catch(() => setData(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dossierId]);

  const addTask = async () => {
    try {
      await api.post(`/admin/coordination/${dossierId}/tasks`, { ...taskForm, due_date: taskForm.due_date || null });
      setTaskForm({ title: "", due_date: "", priority: "normale", internal: false });
      toast.success("Tâche ajoutée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleTask = async (t) => {
    try { await api.patch(`/admin/tasks/${t.id}`, { status: t.status === "termine" ? "a_faire" : "termine" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addVendor = async () => {
    try {
      await api.post(`/admin/coordination/${dossierId}/vendors`, vendorForm);
      setVendorForm({ name: "", category: "", contact: "" });
      toast.success("Prestataire rattaché");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setVendorStatus = async (v, status) => {
    try { await api.patch(`/admin/vendors/${v.id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (data === null) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="coordination-detail-loading">Chargement…</div>;
  if (data === false) return <p className="py-16 text-center text-muted-foreground">Dossier introuvable</p>;

  return (
    <div data-testid="admin-coordination-detail">
      <Link to="/admin/coordination" className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="coordination-back">
        <ArrowLeft className="h-4 w-4" /> Dossiers Coordination
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-deepgreen" data-testid="coordination-title">{data.formule_name} — {data.formule_price.toLocaleString("fr-FR")} €</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.event?.title}{data.event?.date_start ? ` — ${data.event.date_start}` : ""}{data.event?.venue_address ? ` — ${data.event.venue_address}` : ""}
            {" "}· {data.day_j ? "Présence jour J jusqu'au dessert" : "Sans présence jour J"}
          </p>
        </div>
        <StatusBadge status={data.status === "en_cours" ? "En attente" : "Passée"} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="card-ae p-5" data-testid="coordination-tasks">
            <h2 className="font-heading text-lg text-deepgreen">Tâches &amp; check-list</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
              <input className="input-ae" placeholder="Nouvelle tâche…" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title" />
              <input type="date" className="input-ae" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} data-testid="task-due-date" />
              <select className="input-ae" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} data-testid="task-priority">
                <option value="basse">Basse</option><option value="normale">Normale</option><option value="haute">Haute</option>
              </select>
              <button type="button" onClick={addTask} disabled={taskForm.title.length < 2} className="btn-primary-ae !px-4 !py-2.5 !text-xs disabled:opacity-50" data-testid="task-add">
                <Plus className="h-4 w-4" /> Ajouter
              </button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <input type="checkbox" checked={taskForm.internal} onChange={(e) => setTaskForm({ ...taskForm, internal: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="task-internal" />
              Tâche interne Art Event — jamais visible par le client
            </label>
            <ul className="mt-4 space-y-2">
              {data.tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-3 rounded-lg bg-cream px-4 py-3" data-testid={`task-${t.id}`}>
                  <button type="button" onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`} aria-label="Basculer le statut">
                    {t.status === "termine" ? <CheckCircle2 className="h-5 w-5 text-sage" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${t.status === "termine" ? "text-muted-foreground line-through" : "text-deepgreen"}`}>{t.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.due_date ? `Échéance ${t.due_date}` : "Sans échéance"} · <span className={PRIORITY_COLORS[t.priority]}>{t.priority}</span>
                      {t.internal && <span className="ml-2 rounded-full bg-gold-light px-2 py-0.5 text-[10px] font-bold text-deepgreen">INTERNE</span>}
                    </p>
                  </div>
                  <StatusBadge status={TASK_LABELS[t.status]} />
                </li>
              ))}
            </ul>
          </div>

          <div className="card-ae p-5" data-testid="coordination-vendors">
            <h2 className="font-heading text-lg text-deepgreen">Prestataires du mariage</h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
              <input className="input-ae" placeholder="Nom (DJ, photographe, traiteur…)" value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} data-testid="vendor-name" />
              <input className="input-ae" placeholder="Catégorie" value={vendorForm.category} onChange={(e) => setVendorForm({ ...vendorForm, category: e.target.value })} data-testid="vendor-category" />
              <input className="input-ae" placeholder="Contact" value={vendorForm.contact} onChange={(e) => setVendorForm({ ...vendorForm, contact: e.target.value })} data-testid="vendor-contact" />
              <button type="button" onClick={addVendor} disabled={vendorForm.name.length < 2 || vendorForm.category.length < 2} className="btn-primary-ae !px-4 !py-2.5 !text-xs disabled:opacity-50" data-testid="vendor-add">
                <Plus className="h-4 w-4" /> Ajouter
              </button>
            </div>
            <ul className="mt-4 space-y-2">
              {data.vendors.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-cream px-4 py-3" data-testid={`vendor-${v.id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-deepgreen">{v.name} <span className="text-xs font-normal text-muted-foreground">— {v.category}</span></p>
                    {v.contact && <p className="text-xs text-muted-foreground">{v.contact}</p>}
                  </div>
                  <select className="input-ae !w-auto !py-1.5 !text-xs" value={v.status} onChange={(e) => setVendorStatus(v, e.target.value)} data-testid={`vendor-status-${v.id}`} aria-label="Statut prestataire">
                    {Object.entries(VENDOR_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="card-ae p-5" data-testid="coordination-side">
            <h3 className="font-heading text-lg text-deepgreen">Liens du dossier</h3>
            <div className="mt-3 space-y-2 text-sm">
              <Link to="/admin/messages" className="block rounded-lg bg-cream px-4 py-3 font-semibold text-deepgreen hover:bg-sage-light" data-testid="coordination-link-messages">Messages ({data.appointments.length > 0 ? "fils actifs" : "aucun"})</Link>
              <Link to="/admin/rendez-vous" className="block rounded-lg bg-cream px-4 py-3 font-semibold text-deepgreen hover:bg-sage-light" data-testid="coordination-link-appointments">Rendez-vous ({data.appointments.length})</Link>
              <div className="rounded-lg bg-cream px-4 py-3 font-semibold text-deepgreen" data-testid="coordination-link-documents">Documents ({data.documents.length})</div>
              {data.reservation_id && (
                <Link to={`/admin/reservations/${data.reservation_id}`} className="block rounded-lg bg-cream px-4 py-3 font-semibold text-deepgreen hover:bg-sage-light" data-testid="coordination-link-reservation">Réservation Coordination</Link>
              )}
            </div>
          </div>
          <div className="card-ae p-5" data-testid="coordination-notes">
            <h3 className="flex items-center gap-2 font-heading text-lg text-deepgreen"><Clock className="h-4 w-4 text-sage" /> Notes internes</h3>
            <textarea
              className="input-ae mt-3 min-h-24"
              defaultValue={data.internal_notes || ""}
              placeholder="Visibles uniquement par l'équipe Art Event…"
              data-testid="coordination-internal-notes"
              onBlur={async (e) => {
                try { await api.patch(`/admin/coordination/${dossierId}`, { internal_notes: e.target.value }); toast.success("Notes enregistrées"); }
                catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              }}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
